/**
 * Die Blase „Entwickler“ im Chat — der Dreier-Modus. (15.10.2026, shared/entwickler-im-chat.ts)
 *
 * Der Entwickler (die Claude-Sitzung am Server) schreibt als dritte Person in Martins Chat. Seine Blase ist weder Martins (rechts)
 * noch Vesperas (links, mit Avatar und Vorlese-Knopf): sie steht links, trägt immer sichtbar den Namen „Entwickler“ und hat eine
 * eigene, ruhige Form (frontend/style.css, `.bubble.entwickler`, Farben über `--entwickler-*`).
 *
 * ⚠️ Vorgelesen wird sie NUR mit der Stimme des Schmieds (shared/schmied.ts) — nie mit Vesperas: automatisch bei „Entwickler
 *    vorlesen“ (app.ts `schmiedBeitragKam`), und seit 15.10.2026 (Martin, 22:42) auf Tipp über denselben Vorlese-Knopf wie an
 *    Vesperas Blasen (app.ts `makePlayButton(text, …)` mit `entwicklerStimmeJetzt`, übergeben als `knopf`).
 * ⚠️ Barrierefreiheit: `role="group"` mit `aria-label` („Nachricht von Schmied“ / „Nachricht von Zimmermann“) — ein Bildschirmleser
 *    sagt, wer spricht, bevor er liest.
 *
 * 16.10.2026 (Zimmermann P1, shared/entwickler.ts): es gibt mehr als einen Entwickler. Name über der Blase, Rollenbeschriftung und
 * Farbe kommen aus der Liste: der Schmied bleibt „Entwickler“ (unverändert), der Zimmermann heißt „Zimmermann“ und trägt die Klasse
 * `e-zimmermann` (Farben `--entwickler-zimmermann-*`). NUR die Anzeige: ein unbekannter Absender sieht aus wie der Schmied
 * („Entwickler“), WIRD aber nicht wie er behandelt (keine Knöpfe, kein Vorlesen, kein Mikro — app.ts, `entwicklerMerkmal`).
 */
import { baueKopierKnopf } from "./kopier-knopf.js";
import { buildTextBlockCard } from "./karten/system.js";
import { hatKopierBlock, ohneKopierBloecke, teileKopierBloecke } from "../../shared/kopier-bloecke.js";
import { entwickler, istEntwicklerId, type EntwicklerId } from "../../shared/entwickler.js";

/** Der Name über der Blase des Schmieds (seit 19.10.2026 „Schmied“, aus shared/entwickler.ts). */
export const ENTWICKLER_NAME = entwickler("schmied").blasenName;

/** Wie die Blase aussieht: eine bekannte Kennung zeigt ihren Entwickler, alles andere (fehlt, unbekannt) den Schmied. Rein. */
export function anzeigeEntwickler(id: unknown): EntwicklerId {
  return istEntwicklerId(id) ? id : "schmied";
}

/**
 * Baut die Blase (noch nicht eingehängt). `zeichne` setzt den Text (Markdown des Aufrufers) in den Inhaltsknoten; `knopf`
 * baut den Vorlese-Knopf (mit der Stimme des Schmieds) — ohne ihn hat die Blase keinen.
 */
export function baueEntwicklerBlase(
  text: string,
  zeichne: (ziel: HTMLElement, text: string) => void,
  knopf?: (text: string) => HTMLElement,
  /** Ja/Nein-Knöpfe (lib/frage-knoepfe.ts) — nur für einen Beitrag mit `frage` und nur für Konto 1; sonst weglassen. */
  frage?: (text: string) => HTMLElement,
  /** 16.10.2026: wer schreibt (`BlasenMerkmal.entwicklerId`; ohne: der Schmied) — Anzeige über `anzeigeEntwickler`, `data-entwickler` echt. */
  wer: unknown = "schmied",
): HTMLElement {
  const e = entwickler(anzeigeEntwickler(wer));
  const node = document.createElement("div");
  node.className = "bubble entwickler";
  // Der Schmied behält seine Klassen; jeder weitere bekommt `e-<id>` (Farben in style.css).
  if (e.id !== "schmied") node.classList.add(`e-${e.id}`);
  node.setAttribute("role", "group");
  // Ein UNBEKANNTER Absender trägt nie den Namen des Schmieds (Fremdtext darf nicht wie er wirken): neutral „Entwickler“.
  const bekannt = wer === "schmied" || istEntwicklerId(wer);
  const anzeigeName = bekannt ? e.blasenName : "Entwickler";
  node.setAttribute("aria-label", `Nachricht von ${anzeigeName}`);
  node.dataset.speaker = anzeigeName.toUpperCase();
  // Der ECHTE Absender (nicht die Anzeige): „schmied“, eine bekannte Kennung oder „?“ — die Warteblase des Schmieds räumt nur bei
  // „schmied“ (lib/schmied-warteblase.ts).
  node.dataset.entwickler = wer === "schmied" || istEntwicklerId(wer) ? (wer) : "?";
  node.dataset.text = text.slice(0, 80).trim();
  const name = document.createElement("span");
  name.className = "bubble-entwickler-name";
  name.textContent = anzeigeName;
  node.appendChild(name);
  const inhalt = document.createElement("div");
  inhalt.className = "bubble-content";
  // 17.10.2026 (Martin 14:47): Kopier-Blöcke (``` … ```, shared/kopier-bloecke.ts) stehen als Textblock mit Kopieren-Knopf — dieselbe
  // Karte wie Vesperas „Liefertext“ (karten/system.ts). Der Text darin geht nur über `textContent` in die Seite (kein HTML).
  // Nur der Schmied: ein anderer oder unbekannter Absender bekommt keine Karte „Zum Weitergeben“ (Fremdtext darf nicht offiziell wirken).
  const mitBlock = wer === "schmied" && hatKopierBlock(text);
  if (mitBlock) {
    for (const teil of teileKopierBloecke(text)) {
      if (teil.art === "block") {
        const karte = buildTextBlockCard({ ok: true, text: teil.text, title: "Zum Weitergeben" });
        karte.classList.add("entwickler-kopierblock");
        inhalt.appendChild(karte);
      } else {
        const absatz = document.createElement("div");
        zeichne(absatz, teil.text);
        inhalt.appendChild(absatz);
      }
    }
  } else zeichne(inhalt, text.trim());
  node.appendChild(inhalt);
  if (mitBlock) node.classList.add("mit-kopierblock");
  // 17.10.2026 (Martin): Kopier-Knopf — nur der Text der Blase (lib/kopier-knopf.ts), bei JEDEM Entwickler. Mit Kopier-Block entfällt er:
  // der Block hat seinen eigenen Knopf, und der Blasen-Knopf würde die Zaun-Zeichen mitkopieren (verwirrend, doppelt).
  if (text.trim() && !mitBlock) node.appendChild(baueKopierKnopf(text.trim()));
  // Vorlesen: der Block wird nicht vorgelesen, nur der Satz drumherum.
  if (knopf && text.trim()) node.appendChild(knopf(ohneKopierBloecke(text.trim())));
  if (frage && text.trim()) node.appendChild(frage(text.trim()));
  return node;
}

/*
 * DER VORLESE-STAND (16.10.2026) — nur bei „Entwickler vorlesen“: neben dem Namen steht klein, ob der Beitrag noch vorgelesen
 * wird, schon vorgelesen ist oder still blieb (keine Stille, ausgeschaltet, abgebrochen). Als TEXT, nicht nur als Farbe — ein
 * Bildschirmleser liest ihn nach dem Namen. Gesetzt nur für live gekommene Beiträge (app.ts `schmiedBeitragKam`); Blasen aus
 * dem Verlauf tragen keinen Stand. Aussehen: frontend/style.css `.bubble-entwickler-stand`.
 */
export type VorlesenStand = "wartet" | "steht-an" | "vorgelesen" | "nicht-vorgelesen";

const STAND_TEXT: Readonly<Record<VorlesenStand, string>> = {
  // 20.10.2026 (Martin): wer noch nicht dran war und wer still blieb, trägt dasselbe Wort — beides heißt „Martin hat es nicht gehört“.
  wartet: "unvorgelesen",
  "steht-an": "wird vorgelesen",
  vorgelesen: "vorgelesen",
  "nicht-vorgelesen": "unvorgelesen",
};

/** Setzt (oder aktualisiert) den Vorlese-Stand an einer Blase aus `baueEntwicklerBlase`. */
export function zeigeVorlesenStand(blase: HTMLElement, stand: VorlesenStand): void {
  let el = blase.querySelector<HTMLElement>(".bubble-entwickler-stand");
  if (!el) {
    el = document.createElement("span");
    el.className = "bubble-entwickler-stand";
    const name = blase.querySelector(".bubble-entwickler-name");
    if (name) name.after(el);
    else blase.prepend(el);
  }
  blase.dataset.vorgelesen = stand;
  el.textContent = STAND_TEXT[stand];
}

/** Ein Beitrag, der gar nicht erst in die Vorlese-Reihe kommt (Vorlesen aus, lange Spur, nach „Fertig“): „unvorgelesen“ — so sieht Martin, dass nichts kam. */
export function setzeUnvorgelesen(blase: HTMLElement | null): void {
  if (blase) zeigeVorlesenStand(blase, "nicht-vorgelesen");
}
